import React from 'react';
import {devicePixelRatio, hasObjectFitSupport, hasObjectPositionSupport} from '../environment.js';
import * as Transition from '../simpletransition.js';
import {reisy, use} from 'reisy';
import PropTypes from 'prop-types';

export const styles = reisy`
@namespace Image;

Container {
	position: relative;
}
AbsoluteContainer {
	@extends Helpers.AbsFull;
}
ContainerCover {
	overflow: hidden;
}
ContainerPlaceholder {
 	height: 100%;
	background-color: #eee;
}
image {
	display: block;
	max-width: 100%;
}
cover {
	width: 100%;
	height: 100%;
	object-fit: cover;
}
fadein {
	will-change: opacity;
	transition: opacity $Consts.Transition;
	opacity: 0;
}
backgroundImage {
	width: 100%;
	height: 100%;
	background-size: cover;
	background-position: center center;
}
hide {
	display: none;
}
`;

// filter image variants by aspect ratio
function filterRatio(variants, _ratio) {
	if (!variants || !variants.length || !_ratio) { return variants; }
	const ratio = _ratio || (variants[0].size[0] / variants[0].size[1]);
	const filtered = variants.filter(({size}) => (size[0] / size[1] === ratio));
	// if we don’t have images with matching aspect ratio, just return all of
	// them -_-
	return filtered.length ? filtered : variants;
}

export function chooseVariant(img, options = {}) {
	const {
		width = IS_BROWSER ? window.innerWidth : 1024,
		ratio,
		pickClosest = false
	} = options;
	const variants = filterRatio(img.variants || img.versions, ratio);
	let i;
	for (i = 0; i < variants.length; i++) {
		const img = variants[i];
		if (img.size[0] >= width * devicePixelRatio) {
			break;
		}
	}

	i = Math.min(i, variants.length - 1);

	if (i > 0 && pickClosest) {
		// sometimes, it's preferable to choose an image size smaller than the
		// required pixel width, if the distance between available sizes and the
		// requested size is smaller in that direction – e.g. sometimes, we prefer
		// smaller images for performance reasons.
		const dr = devicePixelRatio || 1;
		const pw = variants[i - 1].size[0],
		  nw = variants[i].size[0];
		
		const pp = i;
		i = Math.abs(pw - width * dr) < Math.abs(nw - width * dr) ? i - 1 : i;
	  }
	

	return variants[i];
}

const MARGIN = 500;

function checkInViewport(elem) {
	if (!elem.offsetParent) { return false; }
	const viewport = {
		top: -MARGIN,
		right: window.innerWidth + MARGIN,
		bottom: window.innerHeight + MARGIN,
		left: -MARGIN,
	};
	const rect = elem.getBoundingClientRect();

	return rect.right >= viewport.left && rect.bottom >= viewport.top &&
		rect.left <= viewport.right && rect.top <= viewport.bottom;
}

class Image extends React.PureComponent {
	img = React.createRef();
	elem = React.createRef();
	
	constructor(props) {
		super(props);
		this.state = {
			inViewport: !IS_BROWSER || !props.lazy,
		};
		this.listener = () => {
			if (this.state.inViewport) { return true; }
			if (!checkInViewport(this.elem.current)) { return false; }
			this.setState({
				inViewport: true,
			});
			return true;
		};
		this.loaded = this.loaded.bind(this);
	}
	componentWillMount() {
		this.componentWillReceiveProps(this.props);
	}
	componentWillReceiveProps(props) {
		if (props.image === this.props.image) { return; }
		this.setState({
			inViewport: !IS_BROWSER || !props.lazy,
		});
	}
	componentDidMount() {
		if (!IS_BROWSER) { return; }
		this.componentDidUpdate();
	}
	componentWillUnmount() {
		window.removeEventListener('scroll', this.listener, true);
		window.removeEventListener('resize', this.listener);
	}
	componentDidUpdate() {
		this.componentWillUnmount();
		const {lazy, image} = this.props;
		if (image && lazy) {
			if (!this.listener()) {
				window.addEventListener('scroll', this.listener, true);
				window.addEventListener('resize', this.listener);
			}
		}
	}
	loaded() {
		const {img} = this;
		img.current.style.opacity = 1;
		Transition.end(img.current, () => {
			img.current.style.willChange = 'auto';
		});
	}
	render() {
		const {
			image,
			name = null,
			sizes,
			ratio,
			availWidth,
			style,
			alt = '',
			lazy = false, placeholder = false, cover = false, absolute = false, imageOptions = false,
		} = this.props;
		const {inViewport} = this.state;
		let img;

		// syntax is same as background-position e.g. 'top center' or '0 10%'
		const position = imageOptions && imageOptions.position;

		if (image && inViewport) {
			// copy object without refrencing original image
			let filteredImage = JSON.parse(JSON.stringify(image));
			// filter images by name set on image
			filteredImage.variants = image.variants.filter(img => name ? img.name && img.name.indexOf(name) !== -1 : !img.name);
			// if image list empty fallback to regular images
			if (!filteredImage.variants.length) { filteredImage = image; }

			const variant = chooseVariant(filteredImage, {width: availWidth, ratio});
			const variants = filterRatio(filteredImage.variants, ratio);
			const props = {
				onLoad: this.loaded,
				alt,
				ref: this.img,
				src: variant.url,
				srcSet: variants.map(img => `${img.url} ${(img.size[0] || img.size[1])}w`).join(', '),
				sizes: sizes || `${(variant.size[0] || variant.size[1])}px`,
			};

			if (!cover || (hasObjectFitSupport && !position) || (hasObjectPositionSupport && position)) {
				img = (<img {...props} {...use(styles.image, IS_BROWSER && lazy && styles.fadein, cover && styles.cover)} style={position ? {objectPosition: `${position}`} : {}} />);
			} else {
				// Background image fallback
				// Load actual image inside to detect when loaded (doesn't work on non img elements)
				img = (<div ref={props.ref} {...use(styles.backgroundImage, IS_BROWSER && lazy && styles.fadein, cover)} style={{
						backgroundImage: `url("${variant.url}")`,
						backgroundPosition: position ? position : '',
					}}><img alt={alt} {...use(styles.hide)} onLoad={props.onLoad} src={variant.url} /></div>);
			}
		}
		return (
			<div ref={this.elem} {...use(
				absolute ? styles.AbsoluteContainer : styles.Container,
				cover && styles.ContainerCover,
				placeholder && styles.ContainerPlaceholder,
				style)}>
				{img}
			</div>
		);
	}
}

Image.propTypes = {
	image: PropTypes.object,
	sizes: PropTypes.string,
	ratio: PropTypes.number,
	availWidth: PropTypes.number,
	style: PropTypes.any,
	lazy: PropTypes.bool,
	placeholder: PropTypes.bool,
	cover: PropTypes.bool,
	absolute: PropTypes.bool,
};

export default Image;



// WEBPACK FOOTER //
// ./lib/components/image.js